import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, BookOpen, Calculator, Sparkles, Sun, Cross, Home, Briefcase,
  MessageSquare, ExternalLink, GraduationCap, Music
} from 'lucide-react';

const AREAS = [
  { key: 'landing',    label: 'Public homepage',     icon: Home,          desc: 'The first thing families see. Landing page + marketing.', href: '/' },
  { key: 'hub',        label: 'Parent Dashboard',    icon: BookOpen,      desc: 'The main dashboard where parents manage kids, plans and learning.', href: '/dashboard' },
  { key: 'materials',  label: 'Materials Generator', icon: Sparkles,      desc: 'AI-generated worksheets, coloring pages, and lessons parents love.', href: '/materials' },
  { key: 'summer',     label: 'Summer Slide',        icon: Sun,           desc: 'The summer-break plan — story, worksheet, and check-in.', href: '/beat-the-summer-slide' },
  { key: 'christian',  label: 'Christian Education', icon: Cross,         desc: 'The devotion module — stories, Bible facts, prayer prompts.', href: '/christian' },
  { key: 'careers',    label: 'Careers page',        icon: Briefcase,     desc: 'The public careers page where you were hired from.', href: '/careers' },
  { key: 'chat',       label: 'Team Chat',           icon: MessageSquare, desc: 'The internal team chat with @mentions.', href: '/employee/chat' },
  { key: 'social',     label: 'Social Hub',          icon: Music,         desc: 'Compose, schedule and publish posts to TikTok, Instagram, Facebook and Snapchat.', href: '/social-hub' },
];

const InternPreviewCenter = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/intern/dashboard')} data-testid="preview-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to my tasks
            </Button>
            <h1 className="font-semibold text-slate-800 mt-1 flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-emerald-500" /> Explore the platform
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Hey {user?.name?.split(' ')[0] || 'friend'} — you have a comp&apos;d Premium account. Click any tile below to explore
              the actual product families experience.
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {AREAS.map((a) => {
            const Icon = a.icon;
            return (
              <Link key={a.key} to={a.href} data-testid={`preview-area-${a.key}`}>
                <Card className="border-0 shadow-sm hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer h-full">
                  <CardContent className="p-5">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5" />
                    </div>
                    <p className="font-semibold text-slate-800 flex items-center gap-1">
                      {a.label}
                      <ExternalLink className="w-3 h-3 text-slate-400" />
                    </p>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">{a.desc}</p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        <Card className="border-emerald-200 bg-emerald-50/50 mt-6">
          <CardContent className="p-4 flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-slate-800 text-sm">Preview like a real parent</p>
              <p className="text-xs text-slate-600 mt-1">
                Want to see what a child&apos;s experience feels like end-to-end? Head to <Link to="/dashboard" className="underline font-medium">Parent Dashboard</Link> and add a demo child — you can delete it anytime. Everything you build for Zazu should feel great from that first-child moment.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default InternPreviewCenter;
